import { useState, type FormEvent } from "react";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth.tsx";
import { ApiError } from "../lib/api.ts";
import { Banner, Spinner, errorText } from "../components/ui.tsx";
import { fmtTime } from "../lib/format.ts";
import { useUploads } from "../recording/hooks.ts";

export function LoginPage() {
  const { login, completeNewPassword, expired } = useAuth();
  const up = useUploads();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgot, setForgot] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (challenge) {
        if (newPassword !== repeat) throw new Error("Пароли не совпадают");
        if (newPassword.length < 8 || !/\p{L}/u.test(newPassword) || !/\d/.test(newPassword)) throw new Error("Пароль — не короче 8 символов, буквы и цифры");
        await completeNewPassword(email.trim(), challenge, newPassword);
      } else {
        const r = await login(email.trim(), password);
        if (r.challenge) setChallenge(r.session ?? "");
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "LOCKED" && err.data.lockedUntil) {
        setError(`${err.message}. Попробуйте после ${fmtTime(String(err.data.lockedUntil))}`);
      } else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-4 py-8 bg-canvas">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <div className="w-11 h-11 rounded-full bg-violet flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-[20px] font-bold">AiSol Med</div>
            <div className="text-[11px] uppercase tracking-wider text-mutedt">платформа для клиник</div>
          </div>
        </div>
        <form onSubmit={submit} className="card p-5 space-y-4">
          <h1 className="text-[18px] font-bold">{challenge ? "Придумайте новый пароль" : "Вход"}</h1>
          {expired && !challenge && <Banner kind="warn">Сессия завершена — войдите снова. Идущая запись не остановлена.</Banner>}
          {up.pendingSegments > 0 && !challenge && <Banner kind="info">На телефоне есть недогруженная запись приёма — она догрузится после входа того же врача.</Banner>}
          {!challenge ? (
            <>
              <label className="block">
                <span className="label">Email</span>
                <input className="input" type="email" autoComplete="username" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </label>
              <label className="block">
                <span className="label">Пароль</span>
                <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </label>
            </>
          ) : (
            <>
              <p className="text-[13px] text-mutedt">Временный пароль меняется при первом входе. Не короче 8 символов, буквы и цифры.</p>
              <label className="block">
                <span className="label">Новый пароль</span>
                <input className="input" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoFocus />
              </label>
              <label className="block">
                <span className="label">Повторите пароль</span>
                <input className="input" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
              </label>
            </>
          )}
          {error && <Banner kind="err">{error}</Banner>}
          <button className="btn-primary w-full" disabled={busy}>
            {busy && <Spinner className="w-4 h-4" />} {challenge ? "Сохранить и войти" : "Войти"}
          </button>
          {!challenge && (
            <button type="button" className="btn-link text-[13px]" onClick={() => setForgot((v) => !v)}>
              Забыли пароль?
            </button>
          )}
          {forgot && <div className="text-[13px] text-mutedt">Обратитесь к администратору клиники — он выдаст новый временный пароль.</div>}
        </form>
      </div>
    </div>
  );
}
